import React, { useEffect, useMemo, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import "./CompanyChatWidget.css";

const CHAT_API = process.env.REACT_APP_HRMS_API_URL
  || (process.env.REACT_APP_HRMS_URL
    ? `${process.env.REACT_APP_HRMS_URL}/api`
    : (process.env.REACT_APP_API_URL || "http://localhost:4001/api").replace(/:4001\/api/, ":5000/api").replace(/:3001\/api/, ":5000/api"));

function getChatToken() {
  return (
    localStorage.getItem("company_chat_token") ||
    localStorage.getItem("hrms_token")
  );
}

function initials(name = "User") {
  return String(name)
    .split(" ")
    .map(part => part[0])
    .join("")
    .slice(0, 2)
    .toUpperCase();
}

function parseMeta(value) {
  if (!value) return {};
  if (typeof value === "object") return value;
  try { return JSON.parse(value); } catch { return {}; }
}

function eventUrl() {
  const token = getChatToken();
  return token ? `${CHAT_API}/company-chat/events?token=${encodeURIComponent(token)}` : "";
}

function currentPath() {
  return window.location.pathname.toLowerCase();
}

function chatAllowedOnPath(path) {
  if (path === "/login" || path.startsWith("/login/")) return false;
  if (path === "/apps" || path.startsWith("/apps/")) return false;
  if (path === "/sso" || path.startsWith("/sso/")) return false;
  if (path.startsWith("/candidate-onboarding")) return false;
  if (window.location.port === "5175" && path === "/") return false;
  return true;
}

function prepareChatNotifications() {
  if (!("Notification" in window)) return;
  // /sw.js is registered once in index.js — re-registering it here churns the
  // root-scope registration. Request permission as the very first statement:
  // Safari only honours it while the user gesture that led here is still
  // active, and any await beforehand spends that activation.
  if (Notification.permission === "default") {
    try { Notification.requestPermission(); } catch { /* unsupported */ }
  }
}

async function showChatNotification(payload) {
  if (!("Notification" in window) || Notification.permission !== "granted") return;
  const preview = payload.notification || {};
  const title = preview.title || payload.sender_name || "Company Chat";
  const body = preview.body || payload.body || "New message";
  const options = {
    body,
    tag: `company-chat-${payload.conversation_id || "message"}`,
    data: { url: "/" },
  };

  if ("serviceWorker" in navigator) {
    const registration = await navigator.serviceWorker.ready.catch(() => null);
    if (registration?.showNotification) {
      await registration.showNotification(title, options);
      return;
    }
  }

  new Notification(title, options);
}

async function chatFetch(path, options = {}) {
  const token = getChatToken();
  if (!token) throw new Error("Chat login not found");
  const headers = { ...(options.headers || {}), Authorization: `Bearer ${token}` };
  if (!(options.body instanceof FormData)) headers["Content-Type"] = "application/json";
  const res = await fetch(`${CHAT_API}/company-chat${path}`, { ...options, headers });
  const data = options.raw ? null : await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(data?.error || "Chat request failed");
  return data;
}

export default function CompanyChatWidget() {
  const location = useLocation();
  const [path, setPath] = useState(() => currentPath());
  const [open, setOpen] = useState(false);
  const [view, setView] = useState("list");
  const [me, setMe] = useState(null);
  const [conversations, setConversations] = useState([]);
  const [active, setActive] = useState(null);
  const [messages, setMessages] = useState([]);
  const [message, setMessage] = useState("");
  const [search, setSearch] = useState("");
  const [people, setPeople] = useState([]);
  const [groupName, setGroupName] = useState("");
  const [groupMembers, setGroupMembers] = useState([]);
  const [typingUsers, setTypingUsers] = useState({});
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [listening, setListening] = useState(false);
  const [keepSignedIn, setKeepSignedIn] = useState(() => localStorage.getItem("company_chat_keep_signed_in") === "1");
  // Draggable FAB state — default to right side, vertically centred
  const [fabPos, setFabPos] = useState(() => {
    try {
      const saved = localStorage.getItem("chat_fab_pos");
      if (saved) return JSON.parse(saved);
    } catch {}
    return { x: window.innerWidth - 64, y: Math.round(window.innerHeight * 0.45) };
  });
  const dragState = useRef({ active: false, moved: false, ox: 0, oy: 0 });
  const fileRef = useRef(null);
  const scrollRef = useRef(null);
  const typingTimerRef = useRef(null);
  const activeRef = useRef(null);
  const openRef = useRef(false);
  const chatContainerRef = useRef(null);
  const bootstrapDebounceRef = useRef(null);
  const currentRouterPath = location.pathname.toLowerCase();
  const chatReady = Boolean(getChatToken()) && chatAllowedOnPath(currentRouterPath);

  const unread = useMemo(
    () => conversations.reduce((sum, row) => sum + Number(row.unread_count || 0), 0),
    [conversations]
  );

  async function loadBootstrap() {
    if (!getChatToken()) return;
    try {
      const data = await chatFetch("/bootstrap");
      setMe(data.me);
      setConversations(data.conversations || []);
      setError("");
    } catch (err) {
      setError(err.message);
    }
  }

  async function loadMessages(conversation = active) {
    if (!conversation) return;
    try {
      const data = await chatFetch(`/conversations/${conversation.id}/messages`);
      setMessages(data || []);
      setTimeout(() => scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: "smooth" }), 20);
    } catch (err) {
      setError(err.message);
    }
  }

  // Debounced bootstrap — coalesces rapid SSE events (message+read+conversation) into one fetch
  function debouncedLoadBootstrap() {
    clearTimeout(bootstrapDebounceRef.current);
    bootstrapDebounceRef.current = setTimeout(() => loadBootstrap(), 300);
  }

  useEffect(() => {
    const updatePath = () => setPath(currentPath());
    const originalPushState = window.history.pushState;
    const originalReplaceState = window.history.replaceState;
    window.history.pushState = function pushState(...args) {
      const result = originalPushState.apply(this, args);
      updatePath();
      return result;
    };
    window.history.replaceState = function replaceState(...args) {
      const result = originalReplaceState.apply(this, args);
      updatePath();
      return result;
    };
    window.addEventListener("popstate", updatePath);
    window.addEventListener("hashchange", updatePath);
    return () => {
      window.history.pushState = originalPushState;
      window.history.replaceState = originalReplaceState;
      window.removeEventListener("popstate", updatePath);
      window.removeEventListener("hashchange", updatePath);
    };
  }, []);

  useEffect(() => {
    if (!chatReady) return undefined;
    loadBootstrap();
    // prepareChatNotifications() is deliberately NOT called here. Asking for
    // notification permission on mount has no user gesture behind it, so iOS
    // ignores it and Chrome files it under the quiet prompt. It runs from the
    // chat-open click handler instead, where the gesture is still active.
    const timer = setInterval(() => {
      if (getChatToken()) loadBootstrap();
    }, 60000);
    return () => clearInterval(timer);
  }, [chatReady]);

  useEffect(() => {
    activeRef.current = active;
  }, [active]);

  useEffect(() => {
    openRef.current = open;
  }, [open]);

  // Close chat panel when clicking outside it
  useEffect(() => {
    if (!open) return undefined;
    function handleClickOutside(e) {
      if (chatContainerRef.current && !chatContainerRef.current.contains(e.target)) {
        setOpen(false);
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [open]);

  useEffect(() => {
    if (!chatReady || !open || !active) return undefined;
    loadMessages(active);
  }, [chatReady, open, active?.id]);

  useEffect(() => {
    if (!chatReady) return undefined;
    const url = eventUrl();
    if (!url) return undefined;
    const source = new EventSource(url);

    source.addEventListener("message", event => {
      const payload = JSON.parse(event.data || "{}");
      const currentActive = activeRef.current;
      // Reload messages for the active conversation so the new message appears.
      // NOTE: loadMessages() marks messages as read on the server and triggers a "read"
      // SSE broadcast — but the "read" handler below only calls loadBootstrap (no loadMessages),
      // so there is no cascade.
      if (currentActive?.id && Number(payload.conversation_id) === Number(currentActive.id)) {
        loadMessages(currentActive);
      }
      // Update sidebar unread counts / conversation list (debounced to coalesce rapid events)
      debouncedLoadBootstrap();

      const sentByMe = Number(payload.sender_employee_id) === Number(me?.id);
      const readingCurrentChat = openRef.current
        && document.visibilityState === "visible"
        && currentActive?.id
        && Number(payload.conversation_id) === Number(currentActive.id);

      if (!sentByMe && !readingCurrentChat) showChatNotification(payload).catch(() => {});
    });

    source.addEventListener("conversation", () => {
      // A conversation was created/updated — refresh sidebar only, not messages
      debouncedLoadBootstrap();
    });

    source.addEventListener("read", () => {
      // Someone marked messages as read — update unread counts in sidebar ONLY.
      // Do NOT call loadMessages() here: doing so would trigger another "read" broadcast
      // from the server → SSE "read" fires again → loadMessages() → infinite loop.
      debouncedLoadBootstrap();
    });
    source.addEventListener("typing", event => {
      const payload = JSON.parse(event.data || "{}");
      if (!active?.id || Number(payload.conversation_id) !== Number(active.id) || Number(payload.employee_id) === Number(me?.id)) return;
      setTypingUsers(prev => ({ ...prev, [payload.employee_id]: payload.name || "Someone" }));
      setTimeout(() => {
        setTypingUsers(prev => {
          const next = { ...prev };
          delete next[payload.employee_id];
          return next;
        });
      }, 2500);
    });
    return () => source.close();
  }, [chatReady, active?.id, me?.id]);

  useEffect(() => {
    if (!chatReady || !search.trim()) { setPeople([]); return; }
    const timer = setTimeout(async () => {
      try {
        const data = await chatFetch(`/employees?search=${encodeURIComponent(search.trim())}`);
        setPeople(data || []);
      } catch {
        setPeople([]);
      }
    }, 250);
    return () => clearTimeout(timer);
  }, [chatReady, search]);

  async function openConversation(conversation) {
    setActive(conversation);
    setView("chat");
    await loadMessages(conversation);
  }

  async function startDirectChat(employee) {
    setLoading(true);
    try {
      const data = await chatFetch("/direct", {
        method: "POST",
        body: JSON.stringify({ employee_id: employee.id }),
      });
      await loadBootstrap();
      await openConversation({ id: data.id, type: "direct", name: employee.name, member_names: employee.name });
      setSearch("");
      setPeople([]);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }

  async function sendMessage() {
    if (!message.trim() || !active) return;
    const body = message;
    setMessage("");
    try {
      await chatFetch(`/conversations/${active.id}/messages`, {
        method: "POST",
        body: JSON.stringify({ body }),
      });
      // Do NOT call loadMessages/loadBootstrap here.
      // The backend broadcasts a "message" SSE event which will trigger
      // loadMessages() + loadBootstrap() in the SSE handler below.
      // Calling them here would also trigger a "read" SSE broadcast which
      // would call loadMessages() again → another "read" broadcast → infinite loop.
    } catch (err) {
      setError(err.message);
      setMessage(body);
    }
  }

  function handleMessageChange(value) {
    setMessage(value);
    if (!active) return;
    clearTimeout(typingTimerRef.current);
    typingTimerRef.current = setTimeout(() => {
      chatFetch(`/conversations/${active.id}/typing`, {
        method: "POST",
        body: JSON.stringify({ is_typing: true }),
      }).catch(() => {});
    }, 250);
  }

  function toggleGroupMember(person) {
    setGroupMembers(prev => {
      if (prev.some(member => member.id === person.id)) return prev.filter(member => member.id !== person.id);
      return [...prev, person];
    });
  }

  async function createGroup() {
    if (!groupName.trim() || groupMembers.length === 0) {
      setError("Add a group name and at least one member");
      return;
    }
    setLoading(true);
    try {
      const data = await chatFetch("/groups", {
        method: "POST",
        body: JSON.stringify({ name: groupName, member_ids: groupMembers.map(member => member.id) }),
      });
      setGroupName("");
      setGroupMembers([]);
      setSearch("");
      setPeople([]);
      await loadBootstrap();
      await openConversation({ id: data.id, type: "group", name: groupName });
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }

  async function uploadFile(file) {
    if (!file || !active) return;
    const form = new FormData();
    form.append("file", file);
    try {
      await chatFetch(`/conversations/${active.id}/attachments`, { method: "POST", body: form });
      await loadMessages();
      await loadBootstrap();
    } catch (err) {
      setError(err.message);
    }
  }

  async function confirmAndUploadFile(file) {
    if (!file) return;
    const approved = window.confirm(`Send "${file.name}" as an attachment?`);
    if (!approved) {
      if (fileRef.current) fileRef.current.value = "";
      return;
    }
    await uploadFile(file);
    if (fileRef.current) fileRef.current.value = "";
  }

  async function shareLocation() {
    if (!active || !navigator.geolocation) return setError("Location sharing is not available on this device");
    navigator.geolocation.getCurrentPosition(async (pos) => {
      try {
        await chatFetch(`/conversations/${active.id}/location`, {
          method: "POST",
          body: JSON.stringify({
            latitude: pos.coords.latitude,
            longitude: pos.coords.longitude,
            accuracy: pos.coords.accuracy,
            label: "Shared current location",
          }),
        });
        await loadMessages();
        await loadBootstrap();
      } catch (err) {
        setError(err.message);
      }
    }, () => setError("Please allow location permission to share location"));
  }

  async function downloadAttachment(messageRow) {
    const token = getChatToken();
    const res = await fetch(`${CHAT_API}/company-chat/attachments/${messageRow.attachment_id}/download`, {
      headers: { Authorization: `Bearer ${token}` },
    });
    if (!res.ok) return setError("Could not open attachment");
    const blob = await res.blob();
    const url = URL.createObjectURL(blob);
    window.open(url, "_blank", "noopener,noreferrer");
    setTimeout(() => URL.revokeObjectURL(url), 30000);
  }

  function toggleKeepSignedIn() {
    const next = !keepSignedIn;
    setKeepSignedIn(next);
    if (next) localStorage.setItem("company_chat_keep_signed_in", "1");
    else localStorage.removeItem("company_chat_keep_signed_in");
  }

  // ── Draggable FAB handlers ──────────────────────────────────
  function onFabPointerDown(e) {
    const clientX = e.touches ? e.touches[0].clientX : e.clientX;
    const clientY = e.touches ? e.touches[0].clientY : e.clientY;
    dragState.current = { active: true, moved: false, ox: clientX - fabPos.x, oy: clientY - fabPos.y };
    if (e.currentTarget?.setPointerCapture && e.pointerId != null) {
      e.currentTarget.setPointerCapture(e.pointerId);
    }
    e.preventDefault();
  }

  function onFabPointerMove(e) {
    if (!dragState.current.active) return;
    const clientX = e.touches ? e.touches[0].clientX : e.clientX;
    const clientY = e.touches ? e.touches[0].clientY : e.clientY;
    const dx = Math.abs(clientX - (fabPos.x + dragState.current.ox));
    const dy = Math.abs(clientY - (fabPos.y + dragState.current.oy));
    if (dx > 4 || dy > 4) dragState.current.moved = true;
    const FAB = 52;
    const x = Math.max(4, Math.min(window.innerWidth - FAB - 4, clientX - dragState.current.ox));
    const y = Math.max(4, Math.min(window.innerHeight - FAB - 4, clientY - dragState.current.oy));
    setFabPos({ x, y });
    e.preventDefault();
  }

  function onFabPointerUp() {
    if (!dragState.current.active) return;
    const wasMoved = dragState.current.moved;
    dragState.current.active = false;
    try { localStorage.setItem("chat_fab_pos", JSON.stringify(fabPos)); } catch {}
    if (!wasMoved) toggleChatPanel();
  }

  function toggleChatPanel() {
    if (!open) prepareChatNotifications();
    setOpen(v => !v);
  }

  function startVoiceInput() {
    const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
    if (!SpeechRecognition) return setError("Voice typing is not supported in this browser.");
    const recognition = new SpeechRecognition();
    recognition.lang = "en-IN";
    recognition.interimResults = false;
    recognition.onstart = () => { setListening(true); setError(""); };
    recognition.onend = () => setListening(false);
    recognition.onerror = () => { setListening(false); setError("Microphone could not start. Check browser permission."); };
    recognition.onresult = event => {
      const spoken = event.results?.[0]?.[0]?.transcript || "";
      setMessage(current => `${current}${current ? " " : ""}${spoken}`);
    };
    recognition.start();
  }

  const titleFor = (row) => row.name || row.member_names || "Company Chat";
  const typingText = Object.values(typingUsers).join(", ");

  if (!chatReady) return null;

  return (
    <div
      className="company-chat"
      ref={chatContainerRef}
      style={{ left: fabPos.x, top: fabPos.y }}
    >
      {open && (
        <section className="company-chat-panel">
          <header className="company-chat-header">
            {view !== "list" && <button className="chat-icon-button" onClick={() => setView("list")} aria-label="Back">‹</button>}
            <div className="chat-title-block">
              <strong>{view === "chat" ? titleFor(active) : view === "group" ? "Create Group" : "Company Chat"}</strong>
              <span>{view === "chat" ? "Secure Ebek workspace" : view === "group" ? "Team or department chat" : "Ebek + Ebekmed unified"}</span>
            </div>
            <button className="chat-icon-button" onClick={() => setOpen(false)} aria-label="Close">×</button>
          </header>

          {error && <div className="chat-error">{error}</div>}

          {view === "list" && (
            <div className="chat-list-view">
              <div className="chat-search">
                <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search employee to chat" />
              </div>

              {people.length > 0 && (
                <div className="chat-people-results">
                  {people.map(person => (
                    <button key={person.id} onClick={() => startDirectChat(person)} disabled={loading}>
                      <span className="chat-avatar">{initials(person.name || person.email)}</span>
                      <span><strong>{person.name}</strong><small>{person.department || person.email}</small></span>
                    </button>
                  ))}
                </div>
              )}

              <div className="chat-tabs">
                <span>Recent</span>
                <button type="button" onClick={() => setView("group")}>New Group</button>
                <span>All</span>
              </div>

              <label className="chat-keep-signed">
                <input type="checkbox" checked={keepSignedIn} onChange={toggleKeepSignedIn} />
                <span>
                  <strong>Keep me signed in for Chat</strong>
                  <small>Only company chat stays active on this device.</small>
                </span>
              </label>

              <div className="chat-conversation-list">
                {conversations.map(row => (
                  <button key={row.id} onClick={() => openConversation(row)} className="chat-conversation">
                    <span className={`chat-avatar ${row.type === "channel" ? "channel" : ""}`}>
                      {row.type === "channel" ? "#" : initials(titleFor(row))}
                    </span>
                    <span className="chat-conversation-copy">
                      <strong>{titleFor(row)}</strong>
                      <small>{row.last_message || (row.type === "channel" ? "Company channel" : "No messages yet")}</small>
                    </span>
                    {Number(row.unread_count) > 0 && <span className="chat-unread">{row.unread_count}</span>}
                  </button>
                ))}
              </div>
            </div>
          )}

          {view === "group" && (
            <div className="chat-list-view">
              <div className="chat-search group-form">
                <input value={groupName} onChange={e => setGroupName(e.target.value)} placeholder="Group name, e.g. Sales Team" />
                <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search employees to add" />
              </div>

              {groupMembers.length > 0 && (
                <div className="chat-selected-members">
                  {groupMembers.map(member => (
                    <button key={member.id} type="button" onClick={() => toggleGroupMember(member)}>
                      {member.name} ×
                    </button>
                  ))}
                </div>
              )}

              <div className="chat-people-results">
                {people.map(person => {
                  const selected = groupMembers.some(member => member.id === person.id);
                  return (
                    <button key={person.id} onClick={() => toggleGroupMember(person)} className={selected ? "selected" : ""}>
                      <span className="chat-avatar">{initials(person.name || person.email)}</span>
                      <span><strong>{person.name}</strong><small>{person.department || person.email}</small></span>
                    </button>
                  );
                })}
              </div>

              <button className="chat-create-group" type="button" disabled={loading} onClick={createGroup}>
                Create Group Chat
              </button>
            </div>
          )}

          {view === "chat" && active && (
            <>
              <div className="chat-messages" ref={scrollRef}>
                {messages.map(row => {
                  const mine = Number(row.sender_employee_id) === Number(me?.id);
                  const meta = parseMeta(row.metadata);
                  return (
                    <div key={row.id} className={`chat-message ${mine ? "mine" : ""}`}>
                      <span className="chat-sender">{mine ? "You" : row.sender_name || "System"}</span>
                      {row.message_type === "location" ? (
                        <a className="chat-bubble location" href={`https://www.google.com/maps?q=${meta.latitude},${meta.longitude}`} target="_blank" rel="noreferrer">
                          <strong>Location shared</strong>
                          <small>Open in Google Maps</small>
                        </a>
                      ) : row.attachment_id ? (
                        <button className="chat-bubble attachment" onClick={() => downloadAttachment(row)}>
                          <strong>{row.original_name || row.body}</strong>
                          <small>{row.mime_type || "Attachment"}</small>
                        </button>
                      ) : (
                        <div className="chat-bubble">{row.body}</div>
                      )}
                    </div>
                  );
                })}
              </div>

              {typingText && <div className="chat-typing">{typingText} typing...</div>}

              <footer className="chat-composer">
                <input ref={fileRef} type="file" hidden onChange={e => confirmAndUploadFile(e.target.files?.[0])} />
                <button className="chat-attach-btn" onClick={() => fileRef.current?.click()} aria-label="Attach file" title="Attach file">+</button>

                <input value={message} onChange={e => handleMessageChange(e.target.value)} onKeyDown={e => e.key === "Enter" && sendMessage()} placeholder="Message company chat" />
                <button className={`chat-mic-btn ${listening ? "recording" : ""}`} onClick={startVoiceInput} aria-label="Voice typing" title="Voice typing">{listening ? "●" : "Mic"}</button>
                <button className="chat-location-btn" onClick={shareLocation} aria-label="Share location" title="Share location">Pin</button>
                <button className="chat-send-btn" onClick={sendMessage} aria-label="Send" title="Send">Send</button>
              </footer>
            </>
          )}
        </section>
      )}

      <button
        className="company-chat-fab"
        aria-label="Open company chat"
        onPointerDown={onFabPointerDown}
        onPointerMove={onFabPointerMove}
        onPointerUp={onFabPointerUp}
        onTouchStart={onFabPointerDown}
        onTouchMove={onFabPointerMove}
        onTouchEnd={onFabPointerUp}
        style={{ touchAction: "none", userSelect: "none" }}
      >
        <span style={{ fontSize: 22 }}>💬</span>
        {unread > 0 && <strong>{unread}</strong>}
      </button>
    </div>
  );
}
